import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';

import { AutoNumeric } from '../../../../common/auto-numeric/AutoNumeric';
import { CommonProps } from './pricing-list/PricingItem';
import { calculateValue } from '../../../../../utils/helper-functions';
import { PricingCalculator } from '../../../../../utils/PricingCalculator';
import { useUserPermissions } from '../../../../../context/user/PermissionsProvider';
import { PoFormLabel, pricingConfig } from '../po-form-config';
import { FormPricingKeys } from './pricing-list/FormPricing';

import formStyles from '../PurchaseOrderForm.module.scss';
import { useAutoPricingState } from './context/AutoPricingProvider';
import { initialAutoPricingState } from '../../../../../constants/constants';

const { min } = pricingConfig[FormPricingKeys.SemiMonthlyTerm].validations;

export const semiMonthlyTermTestId = 'SemiMonthlyTermComponent';

export const SemiMonthlyTerm = (props: CommonProps) => {
  const { index, field, state } = props;
  const { hasAutoPricing } = useUserPermissions();
  const { autoPricings } = useAutoPricingState();
  const [value, setValue] = useState('');
  const { semiMonthlyTerm: apiValue, weeklyRate, weeklyTerm } =
    autoPricings[state] || initialAutoPricingState;
  const [isDefaultFromPO, setIsDefaultFromPO] = useState(
    field.semiMonthlyTerm !== ''
  );

  const weeklyTermWatched = useWatch({ name: `pricing[${index}].weeklyTerm` });
  const weeklyRentWatched = useWatch({ name: `pricing[${index}].weeklyRent` });
  const weeklyWatchedPresent = !!weeklyTermWatched && !!weeklyRentWatched;

  const monthlyRent = () =>
    weeklyRentWatched &&
    calculateValue(
      PricingCalculator.monthlyRent,
      weeklyRentWatched,
      `${PricingCalculator.monthlyMultiplier()}`
    );

  const totalRtoPrice = () =>
    weeklyRentWatched &&
    weeklyTermWatched &&
    calculateValue(
      PricingCalculator.totalRtoPrice,
      weeklyRentWatched,
      weeklyTermWatched
    );

  const monthlyTerm = () =>
    totalRtoPrice() &&
    weeklyRentWatched &&
    calculateValue(
      PricingCalculator.monthlyTerm,
      totalRtoPrice(),
      monthlyRent()
    );

  const semiMonthlyTerm = () =>
    monthlyTerm &&
    calculateValue(PricingCalculator.semiMonthlyTerm, monthlyTerm());

  const fieldName = `pricing[${index}].semiMonthlyTerm`;

  useEffect(() => {
    if (
      weeklyTerm === null &&
      weeklyRate === null &&
      apiValue === null &&
      weeklyWatchedPresent &&
      !isDefaultFromPO
    ) {
      //* Value to be calculated from input change
      const term =
        weeklyWatchedPresent && semiMonthlyTerm() >= '1'
          ? semiMonthlyTerm()
          : '';
      setValue(term);
    } else {
      setValue('');
      setIsDefaultFromPO(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeklyTermWatched, weeklyRentWatched]);

  const imperativeValueCheck =
    !!value ||
    !!apiValue ||
    weeklyRentWatched === '' ||
    weeklyTermWatched === '';

  return useMemo(
    () => (
      <div data-testid={semiMonthlyTermTestId}>
        <label htmlFor={fieldName}>{PoFormLabel.SemiMonthlyTerm}:</label>
        <div className={formStyles.noFillField}>
          <Controller
            as={AutoNumeric}
            name={fieldName}
            defaultValue={field.semiMonthlyTerm}
            {...(imperativeValueCheck && {
              imperativeValue:
                value !== '' ? value : apiValue ? apiValue.toString() : '',
              imperativeMode: true
            })}
            options={{
              minimumValue: 0,
              decimalPlaces: 0,
              ...(!hasAutoPricing && {
                maximumValue: Number.MAX_SAFE_INTEGER
              })
            }}
            placeholder="--"
            disabled
            rules={{ required: true, min }}
          />
        </div>
      </div>
    ),
    [
      fieldName,
      field.semiMonthlyTerm,
      imperativeValueCheck,
      value,
      apiValue,
      hasAutoPricing
    ]
  );
};
